<script lang="ts" setup>
import type { OverflowContainerProps } from './index';

import { cn } from '@/utils';

import IconMaterialSymbolsLightExpandLess from '~icons/material-symbols-light/expand-less';
import IconMaterialSymbolsLightExpandMore from '~icons/material-symbols-light/expand-more';

import TextLink from '../ui/text-link/TextLink.vue';

const props = withDefaults(defineProps<OverflowContainerProps>(), {
	maxHeight: 'max-h-[300px]',
	defaultExpanded: false,
	modelValue: undefined,
	containerClass: '',
	buttonClass: '',
	contentClass: '',
	expandText: 'Ver mais',
	collapseText: 'Ver menos'
});

const emit = defineEmits<{
	'update:modelValue': [value: boolean];
}>();

const expanded = ref(props.modelValue ?? props.defaultExpanded);

const contentContainer = useTemplateRef<HTMLElement>('content');

const isOverflowing = computed(() => {
	const container = contentContainer.value;

	if (!container) return false;

	return container?.scrollHeight > container?.clientHeight;
});

const toggleExpand = () => {
	expanded.value = !expanded.value;
	emit('update:modelValue', expanded.value);
};

watch(
	() => props.modelValue,
	newValue => {
		if (newValue !== undefined && newValue !== expanded.value) {
			expanded.value = newValue;
		}
	}
);

const contentClasses = computed(() => {
	if (!expanded.value) {
		return cn(
			'whitespace-pre-line transition-all duration-1000 ease-in-out',
			props.maxHeight,
			props.contentClass
		);
	}

	return cn(
		'whitespace-pre-line transition-all duration-1000 ease-in-out',
		props.contentClass
	);
});

const showGradient = computed(() => {
	return !expanded.value && isOverflowing.value;
});

const showButton = computed(() => {
	return isOverflowing.value;
});

defineExpose({
	isExpanded: readonly(expanded),
	toggle: toggleExpand
});
</script>

<template>
	<div :class="cn('flex w-full flex-col gap-4', containerClass)">
		<div class="relative max-h-fit overflow-hidden">
			<div ref="content" :class="contentClasses">
				<slot />
			</div>

			<div
				v-show="showGradient"
				class="absolute bottom-0 z-20 h-24 w-full bg-[linear-gradient(180deg,rgba(255,255,255,0)0%,rgba(255,255,255,0.8)100%)]"
				data-allow-mismatch="style"
			/>
		</div>

		<div
			v-show="showButton"
			:class="cn('flex items-start', buttonClass)"
			data-allow-mismatch="style"
			@click="toggleExpand"
		>
			<IconMaterialSymbolsLightExpandLess
				v-if="expanded"
				class="size-6 shrink-0 text-primary transition-transform duration-200"
			/>
			<IconMaterialSymbolsLightExpandMore
				v-else
				class="size-6 shrink-0 text-primary transition-transform duration-200"
			/>
			<TextLink as="p" class="font-sans text-base font-normal text-primary">
				{{ expanded ? collapseText : expandText }}
			</TextLink>
		</div>
	</div>
</template>

<!-- eslint-disable-next-line custom-rules/no-style-tag -->
<style></style>
